/**
 * Spec 004.5 PR1p — `WasmWebGpuEngineAdapter` body.
 * Spec 006.6 T043 (MAD-1371) — large-N GPU-shader-aggregator route.
 *
 * Two paths, selected per-run by spin count:
 *
 * - **Raw-outcome path** (`run_webgpu`, default for small N or when
 *   the plan/config-hash sidecars are absent): the wasm pkg returns a
 *   JSON-encoded `SpinOutcome[]` envelope; the adapter folds it into a
 *   `MetricsResult` via the TS-side `aggregateMetrics`. This
 *   materialises every spin's outcome in JS heap — fine at ≤ a few
 *   hundred k spins, fatal (OOM) at 100M.
 *
 * - **GPU-shader-aggregator path** (`run_webgpu_metrics`, taken when
 *   `nSpins >= METRICS_THRESHOLD` AND the request carries `planJson` +
 *   `configHash`): the GPU reduces the 13 `MetricsResult` slices into
 *   on-device accumulators (`produce_audit_buffer = false`, so the
 *   per-spin wire format collapses to zero bytes); only the few-KB
 *   accumulator buffers are read back. No per-spin bytes ever
 *   materialise in WASM/JS heap, which is what makes 100M+ -spin runs
 *   tractable in a browser tab (spec 006.6 FR-013 / FR-014; SC-002
 *   byte-identity to the `wgpu_native` reference). The export returns
 *   a canonical-JSON `MetricsResult` directly — no JS-side folding.
 */

import { aggregateMetrics } from "./aggregateMetrics";
import { allocateRun, abortRun, releaseRun } from "./runRegistry";
import {
  CancelledError,
  CrossOriginIsolationError,
  EngineError,
  decodeReplayedOutcomes,
  type EngineAdapter,
  type MetricsResult,
  type ProgressCallback,
  type ReplayedOutcome,
  type RunId,
  type SimulationRequest,
} from "./types";
import type { WasmModuleExports } from "./wasmModule";

/**
 * Spin-count threshold at/above which the `webgpu` adapter routes to
 * the GPU-shader-aggregator path (`run_webgpu_metrics`) instead of the
 * raw-outcome `run_webgpu` + CPU-fold path. Below it, the raw path's
 * JS-side `aggregateMetrics` is cheap; at/above it, materialising every
 * `SpinOutcome` in JS heap is the OOM risk the aggregator path avoids.
 * Only consulted when the request also carries `planJson`+`configHash`.
 */
const METRICS_THRESHOLD = 1_000_000;

export class WasmWebGpuEngineAdapter implements EngineAdapter {
  readonly mode = "webgpu" as const;

  constructor(private readonly mod: WasmModuleExports) {}

  run(
    request: SimulationRequest,
    onProgress?: ProgressCallback,
  ): [RunId, Promise<MetricsResult>] {
    const { runId, numericId, signal } = allocateRun();
    const promise = this.executeRun(
      request,
      numericId,
      signal,
      onProgress,
    ).finally(() => {
      releaseRun(runId);
    });
    return [runId, promise];
  }

  cancel(runId: RunId): void {
    const entry = abortRun(runId);
    if (entry !== null) {
      // Also signal the wasm side so the in-flight dispatch loop
      // aborts at the next chunk boundary / pre-flight gate.
      this.mod.cancel_run(entry.numericId);
    }
  }

  async sampleWinnings(request: SimulationRequest): Promise<number[]> {
    const { runId, numericId } = allocateRun();
    try {
      const seedBig = parseSeedHex(request.seed);
      const irJson = JSON.stringify(request.config);
      // Force the raw-outcome path (`run_webgpu`, no aggregator): the
      // metrics path never materialises per-spin winnings. Intended for
      // small N, so the JS-heap cost of the SpinOutcome[] envelope is fine.
      const bytes = await this.mod.run_webgpu(
        irJson,
        seedBig,
        request.nSpins,
        numericId,
        null,
      );
      return decodeReplayedOutcomes(bytes).map((o) => o.winnings);
    } catch (err) {
      throw wrapWasmError(err);
    } finally {
      releaseRun(runId);
    }
  }

  async sampleOutcomes(request: SimulationRequest): Promise<ReplayedOutcome[]> {
    // The CPU backend's documented capability boundary: it requires
    // cross-origin isolation (COOP/COEP). Surface the typed error
    // instead of an opaque wasm failure on non-COI WebGPU pages.
    if (globalThis.crossOriginIsolated !== true) {
      throw new CrossOriginIsolationError();
    }
    const { runId, numericId } = allocateRun();
    try {
      const seedBig = parseSeedHex(request.seed);
      const irJson = JSON.stringify(request.config);
      // Deliberately the CPU-WASM raw path even on the webgpu adapter
      // (spec 018 US5): the GPU raw path synthesises a minimal
      // `state_path` without the per-step replay detail (per-evaluate
      // grids, wild-op locked masks), while the CPU IR interpreter
      // emits the full state-machine history. Winnings/grids are
      // byte-identical across backends (Principle II), and N is small.
      const bytes = await this.mod.run_cpu(
        irJson,
        seedBig,
        request.nSpins,
        numericId,
        null,
      );
      return decodeReplayedOutcomes(bytes);
    } catch (err) {
      throw wrapWasmError(err);
    } finally {
      releaseRun(runId);
    }
  }

  private async executeRun(
    request: SimulationRequest,
    numericId: number,
    signal: AbortSignal,
    onProgress?: ProgressCallback,
  ): Promise<MetricsResult> {
    if (signal.aborted) {
      throw new CancelledError();
    }
    const seedBig = parseSeedHex(request.seed);
    const irJson = JSON.stringify(request.config);

    // Route to the GPU-shader-aggregator path for large N when the
    // request carries the plan + config-hash sidecars. This avoids
    // materialising every SpinOutcome in JS heap (the OOM wall at
    // 100M) by reading back only the on-device accumulators.
    const useMetricsPath =
      request.nSpins >= METRICS_THRESHOLD &&
      typeof request.planJson === "string" &&
      typeof request.configHash === "string";

    if (useMetricsPath) {
      // Wrap the progress callback so the streaming-partial 3rd arg
      // (canonical-JSON MetricsResult for spins-so-far) is parsed into a
      // MetricsResult before it reaches the app — matching how the final
      // result is decoded. A malformed partial is dropped (null), never
      // thrown, so a single bad snapshot can't abort the run.
      const wasmProgress = onProgress
        ? (done: number, total: number | null, partialJson?: string) => {
            let partial: MetricsResult | null = null;
            if (typeof partialJson === "string" && partialJson.length > 0) {
              try {
                partial = JSON.parse(partialJson) as MetricsResult;
              } catch {
                partial = null;
              }
            }
            onProgress(done, total, partial);
          }
        : null;
      const wasmCall = this.mod.run_webgpu_metrics(
        irJson,
        request.planJson as string,
        request.configHash as string,
        seedBig,
        request.nSpins,
        numericId,
        wasmProgress,
        // MAD-1983 — forward the perf-investigation layout override (from
        // the `?aggregator=` query param) to the GPU aggregator. `"auto"`
        // and absent both map to the Rust-side adapter-default selection.
        request.aggregatorLayout ?? null,
      );
      let bytes: Uint8Array;
      try {
        bytes = await wasmCall;
      } catch (err) {
        if (signal.aborted) {
          throw new CancelledError();
        }
        throw wrapWasmError(err);
      }
      return decodeMetrics(bytes);
    }

    const wasmCall = this.mod.run_webgpu(
      irJson,
      seedBig,
      request.nSpins,
      numericId,
      onProgress ?? null,
    );
    let bytes: Uint8Array;
    try {
      bytes = await wasmCall;
    } catch (err) {
      if (signal.aborted) {
        throw new CancelledError();
      }
      throw wrapWasmError(err);
    }
    const outcomes = decodeReplayedOutcomes(bytes);
    return aggregateMetrics(outcomes, request.nSpins);
  }
}

// ──────────────────────────────────────────────────────────────────
// Internals (shared with cpu.ts via duplication for now; a future
// refactor can extract into adapter-common.ts if more adapters
// land).
// ──────────────────────────────────────────────────────────────────

function parseSeedHex(seed: string): bigint {
  // Contract: 16-char lowercase hex string encoding a u64.
  if (!/^[0-9a-f]{16}$/.test(seed)) {
    throw new EngineError(
      `Invalid seed: expected 16-char lowercase hex, got "${seed}"`,
      "ENGINE_VALIDATION_FAILED",
    );
  }
  return BigInt(`0x${seed}`);
}

/**
 * Decode the canonical-JSON `MetricsResult` returned by
 * `run_webgpu_metrics`. Unlike `decodeReplayedOutcomes` +
 * `aggregateMetrics`, there is no JS-side folding — the GPU already
 * produced the final `MetricsResult`, so this is a single
 * `JSON.parse` + cast.
 */
function decodeMetrics(bytes: Uint8Array): MetricsResult {
  const decoder = new TextDecoder("utf-8");
  const json = decoder.decode(bytes);
  return JSON.parse(json) as MetricsResult;
}

function wrapWasmError(err: unknown): EngineError {
  const message = err instanceof Error ? err.message : String(err);
  if (message.toLowerCase().includes("cancelled")) {
    return new CancelledError();
  }
  return new EngineError(
    `Wasm dispatch failed: ${message}`,
    "ENGINE_ERROR",
    message,
  );
}
